import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { itineraryApi } from '../services/api';
import { toast } from 'sonner';
import { isNative } from '../utils/capacitor';
import { useAuth } from '../context/AuthContext';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const NotificationToggle = ({ variant = 'default' }) => {
  const { isAuthenticated } = useAuth();
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [supported, setSupported] = useState(true);

  const checkStatus = useCallback(async () => {
    try {
      if (isNative) {
        // On native, push is always supported
        
        if (!isAuthenticated) { setLoading(false); return; }
        const res = await itineraryApi.pushStatus();
        setSubscribed(res.data.subscribed);
        setLoading(false);
        return;
      }

      // Web: check browser support
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
        setSupported(false);
        setLoading(false);
        return;
      }

      const permission = Notification.permission;
      if (permission === 'denied') {
        setSupported(false);
        setLoading(false);
        return;
      }

      
      if (!isAuthenticated) { setLoading(false); return; }

      const res = await itineraryApi.pushStatus();
      setSubscribed(res.data.subscribed);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);  useEffect(() => { checkStatus(); }, [checkStatus]);

  const subscribeNative = async () => {
    setProcessing(true);
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');

      // Request permission
      const permResult = await PushNotifications.requestPermissions();
      if (permResult.receive !== 'granted') {
        toast.error('Push notification permission denied.');
        setProcessing(false);
        return;
      }

      // Register for push
      await PushNotifications.register();

      // Listen for registration token
      PushNotifications.addListener('registration', async (token) => {
        try {
          await itineraryApi.pushSubscribe({
            endpoint: `native://${token.value}`,
            keys: { platform: isNative ? 'native' : 'web', token: token.value },
          });
          setSubscribed(true);
          toast.success('Notifications enabled!');
        } catch {
          toast.error('Failed to register for notifications.');
        }
      });

      PushNotifications.addListener('registrationError', () => {
        toast.error('Push registration failed.');
      });

      // Handle incoming notifications
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        toast.info(notification.title || 'New notification');
      });

    } catch (err) {
      toast.error('Failed to enable notifications.');
    } finally {
      setProcessing(false);
    }
  };

  const subscribeWeb = async () => {
    setProcessing(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notification permission denied. Please enable notifications in your browser settings.');
        setProcessing(false);
        return;
      }

      const { data } = await itineraryApi.getVapidKey();
      const vapidKey = urlBase64ToUint8Array(data.public_key);
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey,
      });

      const subJson = subscription.toJSON();
      await itineraryApi.pushSubscribe({
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });

      setSubscribed(true);
      toast.success('Notifications enabled! You\'ll get alerts for trip updates.');
    } catch (err) {
      toast.error('Failed to enable notifications. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const subscribe = () => isNative ? subscribeNative() : subscribeWeb();

  const unsubscribe = async () => {
    setProcessing(true);
    try {
      if (isNative) {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        await PushNotifications.removeAllListeners();
      } else {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          const subJson = subscription.toJSON();
          await itineraryApi.pushUnsubscribe({
            endpoint: subJson.endpoint,
            keys: subJson.keys,
          });
          await subscription.unsubscribe();
        }
      }
      setSubscribed(false);
      toast.success('Notifications disabled.');
    } catch (err) {
      toast.error('Failed to disable notifications.');
    } finally {
      setProcessing(false);
    }
  };

  if (!supported || loading) return null;
  if (!isAuthenticated) return null;

  if (variant === 'icon') {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={subscribed ? unsubscribe : subscribe}
        disabled={processing}
        className={`relative rounded-full w-9 h-9 ${subscribed ? 'text-gold hover:text-gold-dark' : 'text-charcoal/50 hover:text-charcoal/70'}`}
        title={subscribed ? 'Disable notifications' : 'Enable notifications'}
        data-testid="notification-toggle-icon"
      >
        {processing ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : subscribed ? (
          <>
            <Bell className="w-4 h-4" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-gold rounded-full" />
          </>
        ) : (
          <BellOff className="w-4 h-4" />
        )}
      </Button>
    );
  }

  return (
    <Button
      onClick={subscribed ? unsubscribe : subscribe}
      disabled={processing}
      variant={subscribed ? 'outline' : 'default'}
      size="sm"
      className={subscribed
        ? 'border-gold/30 text-gold-dark hover:bg-gold/10 rounded-full'
        : 'bg-gold hover:bg-gold-light text-ocean rounded-full'}
      data-testid="notification-toggle-btn"
    >
      {processing ? (
        <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
      ) : subscribed ? (
        <Bell className="w-4 h-4 mr-1.5" />
      ) : (
        <BellOff className="w-4 h-4 mr-1.5" />
      )}
      {subscribed ? 'Notifications On' : 'Enable Notifications'}
    </Button>
  );
};
